import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow as ActiveIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

function StatCard({ title, value, subtitle, icon, color = '#1976d2' }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: color + '15',
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" component="div" sx={{ mb: 1, fontWeight: 600 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

interface PeriodStatsProps {
  title: string;
  total: number;
  successful: number;
  failed: number;
  successRate: number;
}

function PeriodStats({ title, total, successful, failed, successRate }: PeriodStatsProps) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Успешность
              </Typography>
              <Typography variant="body2" fontWeight={600} color="success.main">
                {successRate.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={successRate}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: successRate >= 90 ? '#2e7d32' : successRate >= 70 ? '#ed6c02' : '#d32f2f'
                }
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">
              Всего
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {total}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">
              Успешно
            </Typography>
            <Typography variant="h6" fontWeight={600} color="success.main">
              {successful}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">
              Ошибки
            </Typography>
            <Typography variant="h6" fontWeight={600} color="error.main">
              {failed}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function RealtimeSessionsMonitor() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['realtimeSessionsStats'],
    queryFn: statsApi.getRealtimeSessionsStats,
    refetchInterval: 10000, // Обновляем каждые 10 секунд
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Ошибка загрузки данных: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning">
        Нет данных для отображения
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" fontWeight={600}>
          Мониторинг сессий в реальном времени
        </Typography>
        <Chip
          icon={<TimeIcon />}
          label={`Обновлено: ${new Date(data.timestamp).toLocaleTimeString('ru-RU')}`}
          size="small"
          variant="outlined"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Active Sessions Card */}
        <Grid item xs={12} md={4}>
          <StatCard
            title="Активные сессии"
            value={data.active_sessions}
            subtitle="Обрабатываются сейчас"
            icon={<ActiveIcon />}
            color="#ed6c02"
          />
        </Grid>

        {/* Last Hour Period Stats */}
        <Grid item xs={12} md={4}>
          <PeriodStats
            title="За последний час"
            total={data.last_hour.total}
            successful={data.last_hour.successful}
            failed={data.last_hour.failed}
            successRate={data.last_hour.success_rate}
          />
        </Grid>

        {/* Last Day Period Stats */}
        <Grid item xs={12} md={4}>
          <PeriodStats
            title="За последние сутки"
            total={data.last_day.total}
            successful={data.last_day.successful}
            failed={data.last_day.failed}
            successRate={data.last_day.success_rate}
          />
        </Grid>
      </Grid>

      {/* System Status Indicator */}
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {data.active_sessions > 0 ? (
                  <>
                    <ActiveIcon sx={{ color: '#ed6c02', mr: 1 }} />
                    <Typography variant="body1">
                      Система активно обрабатывает запросы
                    </Typography>
                  </>
                ) : (
                  <>
                    <SuccessIcon sx={{ color: '#2e7d32', mr: 1 }} />
                    <Typography variant="body1">
                      Система готова к работе
                    </Typography>
                  </>
                )}
              </Box>

              {data.last_hour.success_rate < 70 && data.last_hour.total > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label="Низкая успешность в последний час"
                  color="error"
                  size="small"
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
